// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// main.ts — bootstrap, screens, gestures, and the auto-pause that makes a fifteen-minute simulation
// legible on a phone.
//
// AUTO-PAUSE IS THE MOST IMPORTANT FEATURE IN THE INTERFACE. It turns a continuous run into roughly
// twenty-five moments where the game stops and tells you one thing. It is also the feature most
// likely to become a nag, so it is bounded four ways: at most one pause per tick with the reasons
// coalesced into a single line, a 150-tick global cooldown, hysteresis on the stock trigger, and a
// set of job ids so any one job can raise at most one banner per run. Unbounded, a single snow batch
// that severs a wing raises a banner per affected job, in the one tick the player most needs to act.

import '@ben-gy/game-engine/mobile.css';
import './styles/main.css';
import { hardenViewport } from '@ben-gy/game-engine/mobile';
import { createSfx } from '@ben-gy/game-engine/sound';
import { createStore } from '@ben-gy/game-engine/storage';
import { hashSeed, newSeed } from '@ben-gy/game-engine/rng';
import { PATCHES } from './cues';
import { Build, BUILDS, DEFAULT_MODE, MODE_IDS, PHASE_NAMES, QUARTER_NAMES } from './modes';
import { COLS } from './map';
import { createBoard, View } from './render';
import { createClock, Clock } from './clock';
import {
  Cmd,
  Command,
  Desig,
  Ev,
  Res,
  State,
  applyCommand,
  createState,
  dayOf,
  daysOfMeals,
  forecast,
  phaseNow,
  quarterOf,
  reachableStock,
  severedCount,
  step,
  stockOf,
} from './sim';
import { FOOTER, aboutHtml, howToPlayHtml, menuHtml, summaryHtml, workRows } from './ui';
import { Ctx, STEPS, STEP_COUNT, allowed, setup as tutorialSetup } from './tutorial';

const store = createStore('larder');
const mixer = createSfx({ muted: store.get('muted', false), patches: PATCHES });

/**
 * AUDIO MUST NEVER BE ABLE TO BREAK THE INTERFACE.
 *
 * Creating or resuming an AudioContext throws on some devices and in some automation environments,
 * and `unlock()` was the FIRST statement in the click handler for the top bar and the tab bar — so a
 * throw there aborted the rest of the handler and every one of those buttons silently did nothing.
 * The board still worked, because it plays its sound after it acts, which is exactly the kind of
 * half-broken that reaches a player: some of the interface responds and some of it just does not.
 */
const sfx = {
  unlock(): void {
    try {
      mixer.unlock();
    } catch {
      /* no audio on this device */
    }
  },
  play(cue: string): void {
    try {
      mixer.play(cue);
    } catch {
      /* no audio on this device */
    }
  },
  muted: () => mixer.muted(),
  setMuted: (m: boolean) => mixer.setMuted(m),
};

const app = document.getElementById('app') as HTMLElement;
app.innerHTML =
  `<div class="main-content"></div>` + `<footer class="site-footer">${FOOTER}</footer>`;
const main = app.querySelector('.main-content') as HTMLElement;

hardenViewport();

// ── app state ───────────────────────────────────────────────────────────────

type Screen = 'menu' | 'game' | 'summary';

let screen: Screen = 'menu';
let s: State | null = null;
let clock: Clock | null = null;
let modeId = store.get('mode', DEFAULT_MODE);
let selected = -1;
let view: View = 'normal';
let focus = -1;
let armed: Build | null = null;
let armedClear = false;
let sheet: 'work' | 'build' | 'alerts' | null = null;
let tutorialOn = false;
let tutorialStep = 0;
let tutorialAfter = '';
let banner = '';
let bannerUntil = 0;
let lastAutoPause = -9999;
const pausedFor = new Set<number>();
let stockLatched = false;
let dirty = true;

const board = createBoard();

function setPlaying(on: boolean): void {
  document.body.classList.toggle('playing', on);
  document.body.classList.toggle('tutoring', on && tutorialOn);
}

// ── screens ─────────────────────────────────────────────────────────────────

function showMenu(): void {
  screen = 'menu';
  setPlaying(false);
  clock?.stop();
  clock = null;
  main.innerHTML = `<section class="menu"></section>`;
  const el = main.querySelector('.menu') as HTMLElement;
  el.innerHTML = menuHtml(modeId);
  syncMute(el);
  el.addEventListener('click', (e) => {
    const b = (e.target as HTMLElement).closest('button') as HTMLButtonElement | null;
    if (!b) return;
    sfx.unlock();
    if (b.dataset.mode) {
      modeId = b.dataset.mode;
      store.set('mode', modeId);
      el.innerHTML = menuHtml(modeId);
      syncMute(el);
      sfx.play('select');
      return;
    }
    switch (b.dataset.act) {
      case 'play':
        startRun(newSeed());
        break;
      case 'tutorial':
        startTutorial();
        break;
      case 'about':
        openModal(aboutHtml());
        break;
      case 'mute':
        sfx.setMuted(!sfx.muted());
        store.set('muted', sfx.muted());
        syncMute(el);
        break;
    }
  });
}

function syncMute(el: HTMLElement): void {
  const b = el.querySelector('[data-act="mute"]') as HTMLButtonElement | null;
  if (b) b.textContent = sfx.muted() ? 'Sound off' : 'Sound on';
}

function gameShell(): void {
  screen = 'game';
  setPlaying(true);
  main.innerHTML =
    `<section class="game">` +
    `<div class="topbar">` +
    `<button data-act="pause" aria-label="Pause">||</button>` +
    `<button data-act="speed" aria-label="Speed">1x</button>` +
    `<button data-act="help" aria-label="How to play">?</button>` +
    `<span class="clockline"></span>` +
    `<button data-act="quit" aria-label="Leave">&#10005;</button>` +
    `</div>` +
    `<div class="supply"></div>` +
    `<div class="forecast"></div>` +
    `<div class="rail"></div>` +
    `<div class="dock"></div>` +
    `<div class="tabs">` +
    `<button data-tab="work" aria-pressed="false">Work</button>` +
    `<button data-tab="build" aria-pressed="false">Build</button>` +
    `<button data-tab="alerts" aria-pressed="false">Alerts</button>` +
    `</div>` +
    `<div class="banner" hidden></div>` +
    // The desktop rail. It exists at every width and the stylesheet only gives it a grid area above
    // 900px — an earlier version declared the grid column in CSS and never created the element, so a
    // 1280px viewport reserved a 360px column and filled it with nothing. That is exactly the class
    // of bug principle #20 exists for: a media query is a distinct product surface, and this one had
    // never once been looked at.
    `<aside class="side" hidden></aside>` +
    `</section>`;
  const g = main.querySelector('.game') as HTMLElement;
  g.insertBefore(board.el, g.querySelector('.dock'));
  wireGame(g);
}

function startRun(seed: number): void {
  tutorialOn = false;
  s = createState(seed, modeId);
  resetRunUi();
  gameShell();
  clock = createClock({ tick: onTick, painted: () => (dirty = true), always: onInterval });
  clock.setSpeed(1);
  paint();
  loopPaint();
}

function startTutorial(): void {
  tutorialOn = true;
  tutorialStep = 0;
  tutorialAfter = '';
  s = tutorialSetup();
  modeId = 'longwinter';
  resetRunUi();
  gameShell();
  clock = createClock({ tick: onTick, painted: () => (dirty = true), always: onInterval });
  clock.setSpeed(0);
  store.set('seenTutorial', true);
  paint();
  loopPaint();
}

function resetRunUi(): void {
  selected = -1;
  view = 'normal';
  focus = -1;
  armed = null;
  armedClear = false;
  sheet = null;
  banner = '';
  lastAutoPause = -9999;
  pausedFor.clear();
  stockLatched = false;
}

function showSummary(): void {
  if (!s) return;
  screen = 'summary';
  setPlaying(false);
  clock?.stop();
  clock = null;
  main.innerHTML = `<section class="summary"></section>`;
  const el = main.querySelector('.summary') as HTMLElement;
  el.innerHTML = summaryHtml(s);
  sfx.play(s.outcome === 1 ? 'ok' : 'knell');
  el.addEventListener('click', (e) => {
    const b = (e.target as HTMLElement).closest('button') as HTMLButtonElement | null;
    if (!b) return;
    if (b.dataset.act === 'again') startRun(newSeed());
    else if (b.dataset.act === 'menu') showMenu();
    else if (b.dataset.act === 'feedback') {
      window.feedback?.open({ returnFocusTo: b });
    }
  });
}

// ── the tick ────────────────────────────────────────────────────────────────

function onTick(): void {
  if (!s) return;
  const before = s.events.length;
  step(s, pending);
  pending = [];
  watchForPause(s, before);
  // The event log is a product, but it is not a memory leak: the summary reads tallies, not history.
  if (s.events.length > 8000) s.events.splice(0, s.events.length - 4000);
  if (s.over) {
    clock?.stop();
    setTimeout(showSummary, 400);
  }
  if (tutorialOn) advanceTutorial();
}

let pending: Command[] = [];

function issue(c: Command): boolean {
  if (!s) return false;
  if (tutorialOn && !allowed(tutorialStep, s, c)) {
    sfx.play('deny');
    return false;
  }
  c.tick = s.tick;
  if (clock && clock.speed() === 0) applyCommand(s, c);
  else pending.push(c);
  dirty = true;
  return true;
}

const PAUSE_COOLDOWN = 150;

function watchForPause(st: State, from: number): void {
  if (!store.get('autopause', true)) return;
  if (st.tick - lastAutoPause < PAUSE_COOLDOWN) return;
  const reasons: string[] = [];
  let cut = 0;
  for (let i = from; i < st.events.length; i++) {
    const e = st.events[i];
    if (e.k === Ev.Death) reasons.push(`${st.name[e.a]} has died`);
    else if (e.k === Ev.Ill) reasons.push(`${st.name[e.a]} has fallen ill`);
    else if (e.k === Ev.PhaseChange && e.a === 2) reasons.push('A storm has begun');
    else if (e.k === Ev.Sever) cut = e.a;
    else if (e.k === Ev.Suspend && !pausedFor.has(e.a)) {
      pausedFor.add(e.a);
      reasons.push(`Work at one tile keeps failing`);
    } else if (e.k === Ev.Arrive) reasons.push('Someone has come out of the snow');
  }
  if (cut > 0) reasons.push(`${cut} building${cut === 1 ? '' : 's'} cut off from the larder`);

  // Hysteresis, or a noisy stock re-crosses the line every few seconds.
  const days = daysOfMeals(st);
  if (!stockLatched && days < 2) {
    stockLatched = true;
    reasons.push('Under two days of food');
  } else if (stockLatched && days > 2.5) stockLatched = false;

  if (reasons.length === 0) return;
  lastAutoPause = st.tick;
  banner = reasons.length === 1 ? reasons[0] : `${reasons[0]} · +${reasons.length - 1} more`;
  bannerUntil = st.tick + 400;
  clock?.setSpeed(0);
  sfx.play('alert');
  dirty = true;
}

// ── painting ────────────────────────────────────────────────────────────────

/**
 * Runs on the interval clock, thirty times a second, PAUSED OR NOT. This is the interface's
 * heartbeat and it is deliberately not requestAnimationFrame: a paused tutorial step advances no
 * ticks, so a step whose completion is only checked inside tick() can never be satisfied — and rAF
 * does not fire at all in a hidden tab, so the whole game would appear frozen to the very browser
 * used to verify it.
 */
function onInterval(): void {
  if (screen !== 'game') return;
  if (tutorialOn) advanceTutorial();
  if (dirty) {
    dirty = false;
    paint();
  }
}

function loopPaint(): void {
  onInterval();
}

function paint(): void {
  if (!s || screen !== 'game') return;
  const g = main.querySelector('.game') as HTMLElement | null;
  if (!g) return;

  board.paint(s, { view, focus, selected, hint: tutorialOn ? STEPS[tutorialStep]?.hint(s) ?? -1 : -1 });

  const sp = clock?.speed() ?? 0;
  (g.querySelector('[data-act="pause"]') as HTMLElement).textContent = sp === 0 ? '▶' : '‖';
  (g.querySelector('[data-act="speed"]') as HTMLElement).textContent = sp === 3 ? '3x' : '1x';
  // Short enough to survive a 375px bar with four 44px buttons beside it.
  (g.querySelector('.clockline') as HTMLElement).innerHTML =
    `<b>d${dayOf(s)}/${s.mode.days}</b> ${QUARTER_NAMES[quarterOf(s)]} · ${PHASE_NAMES[phaseNow(s)]}`;

  const st = s;
  const days = daysOfMeals(st);
  const home = st.cont.length > 0 ? st.cont[0].tile : 0;
  const meals = stockOf(st, Res.Meal);
  const reach = reachableStock(st, Res.Meal, home);
  const cut = severedCount(st);
  (g.querySelector('.supply') as HTMLElement).innerHTML =
    `<span class="days-pill${days < 2 ? ' low' : ''}">larder ${days.toFixed(1)}d</span>` +
    `<span class="stock">wood <b>${stockOf(st, Res.Wood)}</b></span>` +
    `<span class="stock">food <b>${stockOf(st, Res.Food)}</b></span>` +
    `<span class="stock">meals <b>${meals}</b>` +
    (reach < meals ? ` <span class="reach">(${reach} in reach)</span>` : '') +
    `</span>` +
    (cut > 0 ? `<span class="chain">chain ${cut}</span>` : '');

  const fc = forecast(s);
  (g.querySelector('.forecast') as HTMLElement).innerHTML =
    `<span class="fc-label">next</span>` +
    fc.map((p) => `<span class="fc ${PHASE_NAMES[p]}">${PHASE_NAMES[p]}</span>`).join('');

  const alerts = s.alerts.slice(-3).reverse();
  (g.querySelector('.rail') as HTMLElement).innerHTML =
    alerts.length > 0
      ? alerts.map((a) => `<button data-alert="${a.tile}">${a.text}</button>`).join('')
      : `<button class="quiet" disabled>Nothing needs you right now</button>`;

  const dock = g.querySelector('.dock') as HTMLElement;
  dock.innerHTML = Array.from({ length: st.n }, (_, i) => {
    const worst = worstNeed(st, i);
    return (
      `<button class="chip${st.ill[i] ? ' ill' : ''}${st.alive[i] ? '' : ' dead'}" data-who="${i}" aria-pressed="${i === selected}">` +
      `<span class="cname">${st.name[i]}</span>` +
      `<span class="cneed">${st.alive[i] ? worst : 'gone'}</span>` +
      `</button>`
    );
  }).join('');

  const bn = g.querySelector('.banner') as HTMLElement;
  if (banner && s.tick < bannerUntil) {
    bn.hidden = false;
    bn.textContent = banner;
  } else bn.hidden = true;

  paintSide(g, st);
  paintSheet(g);
  paintTutorial(g);
}

/** The desktop rail: the work board and the alerts, always on, never a sheet. */
function paintSide(g: HTMLElement, st: State): void {
  const el = g.querySelector('.side') as HTMLElement | null;
  if (!el) return;
  const wide = window.matchMedia('(min-width: 900px)').matches;
  el.hidden = !wide;
  if (!wide) return;
  const alerts = st.alerts.slice(-6).reverse();
  el.innerHTML =
    `<h3>Work board</h3>${workRows(st)}` +
    `<h3>Alerts</h3>` +
    (alerts.length === 0
      ? `<div class="alertline quiet">Nothing needs you right now.</div>`
      : alerts.map((a) => `<div class="alertline">${a.text}</div>`).join(''));
}

function worstNeed(s: State, i: number): string {
  if (s.ill[i]) return 'ill';
  const pairs: [string, number][] = [
    ['hungry', s.hunger[i]],
    ['cold', s.cold[i]],
    ['tired', s.tired[i]],
  ];
  pairs.sort((a, b) => b[1] - a[1]);
  if (pairs[0][1] < 4000) return 'well';
  return pairs[0][0];
}

// ── sheets ──────────────────────────────────────────────────────────────────

function paintSheet(g: HTMLElement): void {
  if (!s) return;
  g.querySelectorAll('[data-tab]').forEach((b) => {
    (b as HTMLElement).setAttribute('aria-pressed', String((b as HTMLElement).dataset.tab === sheet));
  });
  let el = document.querySelector('.sheet') as HTMLElement | null;
  // ITS OWN backdrop, by its own class. This used to query `.backdrop` and remove whatever it found
  // on every repaint — which meant the moment no sheet was open it deleted the MODAL's backdrop, and
  // tap-outside-to-dismiss silently stopped existing on the how-to-play and about panels. The ✕ and
  // Escape still worked, so nothing looked broken; the overlay just had one fewer way out than the
  // dismissal contract requires.
  let back = document.querySelector('.sheet-back') as HTMLElement | null;
  if (!sheet) {
    el?.remove();
    back?.remove();
    return;
  }
  if (!back) {
    back = document.createElement('div');
    back.className = 'backdrop sheet-back';
    document.body.appendChild(back);
    // pointerdown covers mouse, touch and pen in one handler; armed late so the trailing click of
    // the gesture that OPENED the sheet cannot immediately close it.
    setTimeout(() => {
      back?.addEventListener('pointerdown', () => {
        sheet = null;
        dirty = true;
      });
    }, 350);
  }
  if (!el) {
    el = document.createElement('div');
    el.className = 'sheet';
    document.body.appendChild(el);
    el.addEventListener('click', onSheetClick);
  }
  if (sheet === 'work') {
    el.innerHTML =
      `<button class="close" data-act="closesheet" aria-label="Close">&#10005;</button>` +
      `<h2>Work board</h2>` +
      `<p style="margin:0;color:var(--dim);font-size:12px">Everyone picks the highest thing on this list they can reach.</p>` +
      workRows(s);
  } else if (sheet === 'build') {
    el.innerHTML =
      `<button class="close" data-act="closesheet" aria-label="Close">&#10005;</button>` +
      `<h2>Build &amp; clear</h2>` +
      `<div class="buildgrid">` +
      `<button data-arm="clear" aria-pressed="${armedClear}"><span>Clear</span><span>snow</span></button>` +
      Object.values(BUILDS)
        .map(
          (b) =>
            `<button data-arm="${b.id}" aria-pressed="${armed === b.id}"><span>${b.name}</span><span>${b.wood} wood</span></button>`,
        )
        .join('') +
      `</div>` +
      `<p style="margin:0;color:var(--dim);font-size:12px">Arm one, then drag across the map to mark a whole rectangle.</p>`;
  } else {
    el.innerHTML =
      `<button class="close" data-act="closesheet" aria-label="Close">&#10005;</button>` +
      `<h2>Alerts</h2>` +
      (s.alerts.length === 0
        ? `<p style="color:var(--dim)">Nothing needs you right now.</p>`
        : s.alerts
            .slice()
            .reverse()
            .map((a) => `<div class="workrow"><span class="wmeta">${a.text}</span></div>`)
            .join(''));
  }
}

function onSheetClick(e: Event): void {
  if (!s) return;
  const b = (e.target as HTMLElement).closest('button') as HTMLButtonElement | null;
  if (!b) return;
  if (b.dataset.act === 'closesheet') {
    sheet = null;
    dirty = true;
    return;
  }
  if (b.dataset.up !== undefined || b.dataset.down !== undefined) {
    const i = Number(b.dataset.up ?? b.dataset.down);
    const j = b.dataset.up !== undefined ? i - 1 : i + 1;
    if (j < 0 || j >= s.rank.length) return;
    const order = s.rank.slice();
    [order[i], order[j]] = [order[j], order[i]];
    if (issue({ tick: 0, kind: Cmd.Rank, args: order })) sfx.play('select');
    dirty = true;
    return;
  }
  if (b.dataset.arm !== undefined) {
    if (b.dataset.arm === 'clear') {
      armedClear = !armedClear;
      armed = null;
    } else {
      armed = armed === Number(b.dataset.arm) ? null : (Number(b.dataset.arm) as Build);
      armedClear = false;
    }
    sfx.play('select');
    dirty = true;
  }
}

// ── gestures ────────────────────────────────────────────────────────────────

/**
 * Which tile is under this pointer.
 *
 * `e.target` FIRST, elementFromPoint only as the fallback a drag needs. elementFromPoint returns
 * null in a backgrounded tab — so every tap would be silently dropped, with a clean console and no
 * error anywhere — and the board captures the pointer on down, which makes `target` the cell the
 * gesture started on rather than the one under the finger now. So: target for the press, hit-test
 * for the move.
 */
function tileFrom(e: PointerEvent, moving = false): number {
  if (!moving) {
    const direct = (e.target as HTMLElement | null)?.closest?.('.cell') as HTMLElement | null;
    if (direct) return Number(direct.dataset.t);
  }
  const el = document.elementFromPoint(e.clientX, e.clientY) as HTMLElement | null;
  const cell = el?.closest('.cell') as HTMLElement | null;
  return cell ? Number(cell.dataset.t) : -1;
}

function wireGame(g: HTMLElement): void {
  g.addEventListener('click', (e) => {
    const b = (e.target as HTMLElement).closest('button') as HTMLButtonElement | null;
    if (!b || !s) return;
    sfx.unlock();
    if (b.dataset.tab) {
      sheet = sheet === b.dataset.tab ? null : (b.dataset.tab as typeof sheet);
      dirty = true;
      return;
    }
    if (b.dataset.alert) {
      const t = Number(b.dataset.alert);
      if (t >= 0) {
        focus = t;
        view = 'cost';
      }
      dirty = true;
      return;
    }
    switch (b.dataset.act) {
      case 'pause':
        clock?.toggle();
        sfx.play('speed');
        dirty = true;
        break;
      case 'speed':
        clock?.setSpeed(clock.speed() === 3 ? 1 : 3);
        sfx.play('speed');
        dirty = true;
        break;
      case 'help':
        openModal(howToPlayHtml());
        break;
      case 'quit':
        showMenu();
        break;
    }
  });

  // The desktop rail reuses the sheet's own row handler, so the up/down arrows behave identically
  // whether they are in a bottom sheet or a side panel.
  g.querySelector('.side')?.addEventListener('click', onSheetClick);

  g.querySelector('.dock')?.addEventListener('click', (e) => {
    const b = (e.target as HTMLElement).closest('.chip') as HTMLElement | null;
    if (!b || !s) return;
    const who = Number(b.dataset.who);
    selected = selected === who ? -1 : who;
    view = selected >= 0 ? 'reach' : 'normal';
    focus = selected >= 0 ? s.pos[selected] : -1;
    sfx.play('select');
    dirty = true;
  });

  wireBoard();
}

/**
 * Tap / long-press / drag on the board.
 *
 * A TAP ON A TILE IS NEVER DESTRUCTIVE. It selects, it shades, or it deselects. Everything that
 * commits goes through a control that is itself at least 44px — a wheel sector or a build chip —
 * which is the only reason a 28px cell is legal at all.
 */
function wireBoard(): void {
  const el = board.el;
  let downT = -1;
  let downAt = 0;
  let held: number | null = null;
  let dragging = false;

  el.addEventListener('pointerdown', (e: PointerEvent) => {
    if (!s) return;
    // setPointerCapture THROWS for a pointer id the browser does not consider active, and an
    // exception here aborts the rest of the handler — so downT is never set and the tap is silently
    // lost. Capture is an optimisation for drags leaving the grid; losing it must never lose the tap.
    try {
      el.setPointerCapture(e.pointerId);
    } catch {
      /* no capture: pointermove still reaches us via the document */
    }
    downT = tileFrom(e);
    downAt = e.timeStamp;
    dragging = false;
    if (downT < 0) return;
    if (armed !== null || armedClear) return; // armed: this is a paint-drag, not a long-press
    held = window.setTimeout(() => {
      held = null;
      if (downT >= 0) openWheel(downT, e.clientX, e.clientY);
    }, 350);
  });

  el.addEventListener('pointermove', (e: PointerEvent) => {
    if (downT < 0) return;
    const t = tileFrom(e, true);
    if (t !== downT && t >= 0) {
      dragging = true;
      if (held !== null) {
        clearTimeout(held);
        held = null;
      }
      if (armed !== null || armedClear) previewRect(downT, t);
    }
  });

  const finish = (e: PointerEvent): void => {
    if (held !== null) {
      clearTimeout(held);
      held = null;
    }
    if (!s || downT < 0) {
      downT = -1;
      return;
    }
    const up = tileFrom(e, dragging);
    const quick = e.timeStamp - downAt < 350;
    if ((armed !== null || armedClear) && up >= 0) {
      // A paint-drag, or a single armed tap: both designate a rectangle.
      designate(downT, up);
    } else if (!dragging && quick && up === downT) {
      tapTile(downT);
    }
    clearPreview();
    downT = -1;
    dragging = false;
  };

  el.addEventListener('pointerup', finish);
  // A cancelled gesture is an ABORTED one, not a completed one.
  el.addEventListener('pointercancel', () => {
    if (held !== null) {
      clearTimeout(held);
      held = null;
    }
    clearPreview();
    downT = -1;
    dragging = false;
  });
}

function previewRect(a: number, b: number): void {
  clearPreview();
  forRect(a, b, (t) => board.cells[t].classList.add('hint'));
}

function clearPreview(): void {
  for (const c of board.cells) c.classList.remove('hint');
}

function forRect(t0: number, t1: number, f: (t: number) => void): void {
  const c0 = Math.min(t0 % COLS, t1 % COLS);
  const c1 = Math.max(t0 % COLS, t1 % COLS);
  const r0 = Math.min((t0 / COLS) | 0, (t1 / COLS) | 0);
  const r1 = Math.max((t0 / COLS) | 0, (t1 / COLS) | 0);
  for (let r = r0; r <= r1; r++) for (let c = c0; c <= c1; c++) f(r * COLS + c);
}

function designate(a: number, b: number): void {
  if (!s) return;
  const kind = armedClear ? Desig.Clear : Desig.Build;
  const arg = armedClear ? 0 : (armed as Build);
  if (issue({ tick: 0, kind: Cmd.Designate, args: [a, b, kind, arg] })) sfx.play('place');
  dirty = true;
}

function tapTile(t: number): void {
  if (!s) return;
  // A colonist is selected: this is a one-off order to go there.
  if (selected >= 0 && s.alive[selected]) {
    if (issue({ tick: 0, kind: Cmd.Call, args: [selected, t] })) sfx.play('ok');
    selected = -1;
    view = 'normal';
    focus = -1;
    dirty = true;
    return;
  }
  // A store or the stove: shade the map by distance to it.
  const b = s.build[t];
  if (b === Build.Stockpile + 1 || b === Build.Stove + 1 || b === Build.Firepit + 1) {
    if (view === 'cost' && focus === t) {
      view = 'normal';
      focus = -1;
    } else {
      view = 'cost';
      focus = t;
    }
    sfx.play('select');
    dirty = true;
    return;
  }
  // Somebody standing here.
  for (let i = 0; i < s.n; i++) {
    if (s.alive[i] && s.pos[i] === t) {
      selected = i;
      view = 'reach';
      focus = t;
      sfx.play('select');
      dirty = true;
      return;
    }
  }
  selected = -1;
  view = 'normal';
  focus = -1;
  dirty = true;
}

function openWheel(t: number, x: number, y: number): void {
  if (!s) return;
  document.querySelector('.wheel')?.remove();
  const w = document.createElement('div');
  w.className = 'wheel';
  // Clamped so a corner tile's wheel is never half off the screen.
  const cx = Math.min(Math.max(x, 88), window.innerWidth - 88);
  const cy = Math.min(Math.max(y, 88), window.innerHeight - 88);
  w.style.left = `${cx}px`;
  w.style.top = `${cy}px`;
  w.innerHTML =
    `<button data-w="clear">Clear</button>` +
    `<button data-w="cut">Cut</button>` +
    `<button data-w="build">Build▸</button>` +
    `<button data-w="cancel">Cancel</button>`;
  document.body.appendChild(w);

  const back = document.createElement('div');
  back.className = 'backdrop wheel-back';
  document.body.appendChild(back);
  const close = (): void => {
    w.remove();
    back.remove();
  };
  setTimeout(() => back.addEventListener('pointerdown', close), 350);

  w.addEventListener('click', (e) => {
    const b = (e.target as HTMLElement).closest('button') as HTMLButtonElement | null;
    if (!b || !s) return;
    const k = b.dataset.w;
    if (k === 'clear') issue({ tick: 0, kind: Cmd.Designate, args: [t, t, Desig.Clear, 0] }) && sfx.play('dig');
    else if (k === 'cut') issue({ tick: 0, kind: Cmd.Designate, args: [t, t, Desig.Cut, 0] }) && sfx.play('chop');
    else if (k === 'cancel') issue({ tick: 0, kind: Cmd.Cancel, args: [t, t] }) && sfx.play('deny');
    else if (k === 'build') {
      sheet = 'build';
      sfx.play('select');
    }
    close();
    dirty = true;
  });
  sfx.play('select');
}

// ── modal ───────────────────────────────────────────────────────────────────

function openModal(html: string): void {
  document.querySelector('.modal')?.remove();
  document.querySelector('.modal-back')?.remove();
  const back = document.createElement('div');
  back.className = 'backdrop modal-back';
  const m = document.createElement('div');
  m.className = 'modal';
  m.setAttribute('role', 'dialog');
  m.setAttribute('aria-modal', 'true');
  m.innerHTML = `<button class="close" data-act="x" aria-label="Close">&#10005;</button>${html}`;
  document.body.appendChild(back);
  document.body.appendChild(m);
  const close = (): void => {
    m.remove();
    back.remove();
    document.removeEventListener('keydown', onKey);
  };
  const onKey = (e: KeyboardEvent): void => {
    if (e.key === 'Escape') close();
  };
  // All four exits: the X, an outside tap, Escape, and a backdrop that is genuinely reachable.
  m.addEventListener('click', (e) => {
    if ((e.target as HTMLElement).closest('[data-act="x"]')) close();
  });
  setTimeout(() => back.addEventListener('pointerdown', close), 350);
  document.addEventListener('keydown', onKey);
}

// ── tutorial ────────────────────────────────────────────────────────────────

function ctx(): Ctx {
  return { selected, view, focus };
}

function advanceTutorial(): void {
  if (!s) return;
  const step = STEPS[tutorialStep];
  if (!step) return;
  if (step.done(s, ctx())) {
    tutorialAfter = step.after;
    tutorialStep++;
    sfx.play('ok');
    if (tutorialStep < STEP_COUNT) clock?.setSpeed(STEPS[tutorialStep].runs ? 1 : 0);
    else {
      tutorialOn = false;
      document.body.classList.remove('tutoring');
      clock?.setSpeed(1);
    }
    dirty = true;
  }
}

function paintTutorial(g: HTMLElement): void {
  let el = g.querySelector('.tut') as HTMLElement | null;
  if (!tutorialOn) {
    el?.remove();
    return;
  }
  if (!el) {
    el = document.createElement('div');
    el.className = 'tut';
    g.appendChild(el);
    el.addEventListener('click', (e) => {
      const b = (e.target as HTMLElement).closest('button') as HTMLButtonElement | null;
      if (!b) return;
      if (b.dataset.act === 'skip') {
        tutorialOn = false;
        document.body.classList.remove('tutoring');
        clock?.setSpeed(1);
        dirty = true;
      }
    });
  }
  const step = STEPS[tutorialStep];
  if (!step) return;
  const html =
    `<span class="step">STEP ${tutorialStep + 1} OF ${STEP_COUNT}</span>` +
    `<span class="say">${step.text}</span>` +
    (tutorialAfter && tutorialStep > 0 ? `<span class="after">${tutorialAfter}</span>` : '') +
    `<div class="row"><button data-act="skip">Skip the tutorial</button></div>`;
  if (el.innerHTML !== html) {
    el.innerHTML = html;
    // Reserve exactly as much room as the card actually takes. See the note in main.css.
    document.body.style.setProperty('--tut-h', `${Math.ceil(el.getBoundingClientRect().height)}px`);
  }
}

// ── keyboard ────────────────────────────────────────────────────────────────

document.addEventListener('keydown', (e) => {
  if (screen !== 'game') return;
  if (e.key === ' ') {
    e.preventDefault();
    clock?.toggle();
    dirty = true;
  } else if (e.key === '1') clock?.setSpeed(1);
  else if (e.key === '3') clock?.setSpeed(3);
  else if (e.key === 'Escape') {
    sheet = null;
    dirty = true;
  }
});

// ── the verification hook ───────────────────────────────────────────────────
//
// A read-only window onto the simulation, plus the ability to post a command exactly as the UI does.
// The Step 11 browser pass needs to assert things a screenshot cannot see — that a designation
// really landed, that a tile's snow depth really moved, which tutorial step is live — and driving the
// real `issue()` means the pass exercises the shipped path rather than a parallel one. It reads
// state and posts the same commands a finger does; it cannot reach anything the player cannot.

interface Hook {
  tick(): number;
  step(): number;
  desig(t: number): number;
  depth(t: number): number;
  rank(): number[];
  souls(): number;
  view(): string;
  issue(kind: number, args: number[]): boolean;
  setSpeed(n: number): void;
}

(window as unknown as { __larder: Hook }).__larder = {
  tick: () => s?.tick ?? -1,
  step: () => (tutorialOn ? tutorialStep : -1),
  desig: (t) => s?.desig[t] ?? -1,
  depth: (t) => s?.depth[t] ?? -1,
  rank: () => s?.rank.slice() ?? [],
  souls: () => (s ? s.alive.slice(0, s.n).reduce((a, b) => a + b, 0) : -1),
  view: () => view,
  issue: (kind, args) => issue({ tick: 0, kind: kind as Cmd, args }),
  setSpeed: (n) => clock?.setSpeed(n),
};

// ── boot ────────────────────────────────────────────────────────────────────

const params = new URLSearchParams(location.search);
const w = params.get('w');
if (w) {
  // ?w=longwinter-4f1c2 — the same winter, exactly, for anybody who opens the link.
  const [m, seedStr] = w.split('-');
  if (MODE_IDS.includes(m)) modeId = m;
  startRun(hashSeed(seedStr ?? w));
} else if (!store.get('seenTutorial', false)) {
  // Before the menu, on a fresh device — but skippable in one tap.
  startTutorial();
} else {
  showMenu();
}

